import React from "react"
import {
  formatCurrencyInThousands,
  formatPercent,
  formatRatio,
} from "./tableFormatters"
import {
  ToolTable,
  ToolTableHeader,
  ToolTableBody,
  ToolTableRow,
  ToolTableHead,
  ToolTableCell,
} from "@/ui/ToolTable"

const TopTenPortfolioCompaniesTable = ({ data }: { data: any[] }) => {
  // Normalize data: convert string values to numbers if needed
  const normalizeRow = (row: any) => ({
    portfolio_company: row.portfolio_company,
    series_name: row.series_name,
    qpcsp_weighting: parseFloat(row.qpcsp_weighting) || 0,
    est_calls: parseFloat(row.est_calls) || 0,
    est_distributions: parseFloat(row.est_distributions) || 0,
    est_valuation: parseFloat(row.est_valuation) || 0,
    est_gross_moic:
      row.est_gross_moic !== null && row.est_gross_moic !== undefined
        ? parseFloat(row.est_gross_moic)
        : null,
    is_total: row.is_total || false,
  })

  return (
    <ToolTable>
      <ToolTableHeader>
        <ToolTableRow>
          <ToolTableHead>Portfolio Company</ToolTableHead>
          <ToolTableHead>Series Name</ToolTableHead>
          <ToolTableHead>QPCSP Weighting</ToolTableHead>
          <ToolTableHead>Est. Calls</ToolTableHead>
          <ToolTableHead>Est. Distributions</ToolTableHead>
          <ToolTableHead>Est. Valuation</ToolTableHead>
          <ToolTableHead>Est. Gross MOIC</ToolTableHead>
        </ToolTableRow>
      </ToolTableHeader>
      <ToolTableBody>
        {data.map((row, index) => {
          const normalizedRow = normalizeRow(row)
          return (
            <ToolTableRow
              key={index}
              variant={normalizedRow.is_total ? "totals" : "default"}
            >
              <ToolTableCell>{normalizedRow.portfolio_company}</ToolTableCell>
              <ToolTableCell>{normalizedRow.series_name || "—"}</ToolTableCell>
              <ToolTableCell>
                {formatPercent(normalizedRow.qpcsp_weighting)}
              </ToolTableCell>
              <ToolTableCell>
                {formatCurrencyInThousands(normalizedRow.est_calls)}
              </ToolTableCell>
              <ToolTableCell>
                {formatCurrencyInThousands(normalizedRow.est_distributions)}
              </ToolTableCell>
              <ToolTableCell>
                {formatCurrencyInThousands(normalizedRow.est_valuation)}
              </ToolTableCell>
              <ToolTableCell>
                {normalizedRow.est_gross_moic !== null
                  ? formatRatio(normalizedRow.est_gross_moic)
                  : "—"}
              </ToolTableCell>
            </ToolTableRow>
          )
        })}
      </ToolTableBody>
    </ToolTable>
  )
}

const EmptyTable = () => {
  return (
    <div className="editorjs-empty-tool-table">No table data available</div>
  )
}

const hasValidTableData = (data: any) => {
  return data && Array.isArray(data) && data.length > 0
}

export class TopTenPortfolioCompaniesTableTool {
  api: any
  block: any
  createPortal: any
  removePortal: any
  portal: any
  tableData: any
  headerInserted: boolean

  constructor(toolConfig: any) {
    this.block = toolConfig.block
    this.api = toolConfig.api
    this.createPortal = toolConfig.config?.createPortal
    this.removePortal = toolConfig.config?.removePortal
    this.tableData = toolConfig.config?.tableData

    this.headerInserted = toolConfig.data?.header_inserted || false

    return this
  }

  static get toolbox() {
    return {
      title: "Top 10 Portfolio Companies",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="m16 15-2-2 2-2"/><path d="M21 9H3"/><path d="M21 15H3"/></svg>',
    }
  }

  render() {
    const rootEl = document.createElement("div")

    const hasData = hasValidTableData(this.tableData)

    // Check if we need to insert a header above this block
    queueMicrotask(() => {
      this.checkAndInsertHeaderIfNeeded()
    })

    this.portal = this.createPortal(
      this.block.id,
      hasData ? (
        <TopTenPortfolioCompaniesTable data={this.tableData} />
      ) : (
        <EmptyTable />
      ),
      rootEl,
    )

    return rootEl
  }

  async checkAndInsertHeaderIfNeeded() {
    try {
      // Short-circuit if header has already been inserted
      if (this.headerInserted) {
        return
      }

      // Find this block's index by searching through all blocks
      let currentBlockIndex = -1
      const totalBlocks = this.api.blocks.getBlocksCount()

      for (let i = 0; i < totalBlocks; i++) {
        const block = this.api.blocks.getBlockByIndex(i)
        if (block && block.id === this.block.id) {
          currentBlockIndex = i
          break
        }
      }

      if (currentBlockIndex === -1) {
        console.error(
          "ERROR: Could not find current block, skipping header insertion",
        )
        return
      }

      if (currentBlockIndex > 0) {
        const previousBlock = this.api.blocks.getBlockByIndex(
          currentBlockIndex - 1,
        )

        if (previousBlock) {
          // Await the saved data from the previous block
          const previousBlockData = await previousBlock.save()

          // Check if it's a header block with our specific text and level
          if (
            previousBlockData.tool === "header" &&
            previousBlockData.data.text === "Top 10 Portfolio Companies" &&
            previousBlockData.data.level === 3
          ) {
            // Header exists - mark as inserted even though we didn't insert it
            this.headerInserted = true
            return
          }
        }
      }

      this.insertHeaderBlock(currentBlockIndex)
    } catch (error) {
      console.error("Error in checkAndInsertHeaderIfNeeded:", error)
    }
  }

  insertHeaderBlock(currentBlockIndex: number) {
    try {
      // Insert a header block above the current table block
      this.api.blocks.insert(
        "header",
        {
          text: "Top 10 Portfolio Companies",
          level: 3,
        },
        {},
        currentBlockIndex,
        true,
      )

      this.headerInserted = true

      // Move focus back to the table block after insertion
      setTimeout(() => {
        this.api.caret.setToBlock(currentBlockIndex + 1)
      }, 50)
    } catch (error) {
      console.error("Error inserting header block:", error)
    }
  }

  remove() {
    const totalBlocks = this.api.blocks.getBlocksCount()
    for (let i = 0; i < totalBlocks; i++) {
      const block = this.api.blocks.getBlockByIndex(i)
      if (block && block.id === this.block.id) {
        this.api.blocks.delete(i)
        break
      }
    }
  }

  removed() {
    this.removePortal(this.block.id)
  }

  save() {
    return {
      type: "top_ten_portfolio_companies_table",
      header_inserted: this.headerInserted,
    }
  }

  static get isReadOnlySupported() {
    return true
  }
}
